import { supabase } from './supabaseService';
import { AIInsight } from '../types';
import authService from './authService';

interface AIInsightDB {
  id: string;
  tenant_id: string;
  contact_id: string | null;
  lead_id: string | null;
  insight_type: string;
  title: string;
  description: string;
  confidence: number;
  data: any;
  status: 'pending' | 'accepted' | 'dismissed';
  created_at: string;
  expires_at: string;
  created_by: string;
}

interface AIUsageTracking {
  tenant_id: string;
  user_id: string;
  action: string;
  component: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  estimated_cost: number;
  success: boolean;
  error_message?: string;
}

interface UsageStats {
  totalTokensToday: number;
  totalCostToday: number;
  totalTokensWeek: number;
  totalCostWeek: number;
  totalTokensMonth: number;
  totalCostMonth: number;
  recentUsage: AIUsageTracking[];
}

class AIInsightsService {
  private getCurrentUserId(): string {
    try {
      // Use authService which handles session synchronously
      const currentUser = authService.getCurrentSession()?.user;
      const userId = currentUser?.id || '';

      if (!userId) {
        console.warn('⚠️ No user ID found in current session');
      }

      return userId;
    } catch (error) {
      console.error('❌ Error getting current user ID:', error);
      return '';
    }
  }

  async getValidInsights(tenantId: string, contactIds?: string[], leadIds?: string[]): Promise<AIInsight[]> {
    try {
      let query = supabase
        .from('ai_insights')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (contactIds && contactIds.length > 0) {
        query = query.in('contact_id', contactIds);
      }

      if (leadIds && leadIds.length > 0) {
        query = query.in('lead_id', leadIds);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching insights from Supabase:', error);
        return [];
      }

      return (data || []).map(this.dbToInsight);
    } catch (error) {
      console.error('Error in getValidInsights:', error);
      return [];
    }
  }

  async saveInsight(insight: AIInsight, tenantId: string): Promise<boolean> {
    try {
      const userId = this.getCurrentUserId();

      if (!userId) {
        console.error('❌ Cannot save insight: No user ID available');
        return false;
      }

      if (!tenantId) {
        console.error('❌ Cannot save insight: No tenant ID provided');
        return false;
      }

      console.log('💾 Saving insight:', {
        type: insight.type,
        title: insight.title,
        tenantId,
        userId,
        contactId: insight.contactId
      });

      const dbInsight = {
        tenant_id: tenantId,
        contact_id: insight.contactId || null,
        lead_id: insight.data?.leadId || null,
        insight_type: insight.type,
        title: insight.title,
        description: insight.description,
        confidence: insight.confidence,
        data: insight.data || {},
        status: insight.status,
        expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(), // 1 hour from now
        created_by: userId
      };

      const { data, error } = await supabase
        .from('ai_insights')
        .insert(dbInsight)
        .select();

      if (error) {
        console.error('❌ Error saving insight to Supabase:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        return false;
      }

      console.log('✅ Insight saved successfully to Supabase:', insight.title);
      return true;
    } catch (error) {
      console.error('Error in saveInsight:', error);
      return false;
    }
  }

  async updateInsightStatus(insightId: string, status: 'accepted' | 'dismissed'): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('ai_insights')
        .update({ status })
        .eq('id', insightId);

      if (error) {
        console.error('Error updating insight status:', error);
        return false;
      }

      console.log(`✅ Insight ${insightId} marked as ${status}`);
      return true;
    } catch (error) {
      console.error('Error in updateInsightStatus:', error);
      return false;
    }
  }

  async deleteInsight(insightId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('ai_insights')
        .delete()
        .eq('id', insightId);

      if (error) {
        console.error('Error deleting insight:', error);
        return false;
      }

      console.log(`🗑️ Insight ${insightId} deleted`);
      return true;
    } catch (error) {
      console.error('Error in deleteInsight:', error);
      return false;
    }
  }

  async trackAIUsage(usage: AIUsageTracking): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('ai_usage_tracking')
        .insert(usage);

      if (error) {
        console.error('Error tracking AI usage:', error);
        return false;
      }

      console.log(`📊 AI usage tracked: ${usage.action} - ${usage.total_tokens} tokens - $${usage.estimated_cost.toFixed(4)}`);
      return true;
    } catch (error) {
      console.error('Error in trackAIUsage:', error);
      return false;
    }
  }

  async getUsageStats(tenantId: string, userId?: string): Promise<UsageStats> {
    try {
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      let query = supabase
        .from('ai_usage_tracking')
        .select('*')
        .eq('tenant_id', tenantId);

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query.order('timestamp', { ascending: false });

      if (error) {
        console.error('Error fetching usage stats:', error);
        return this.getEmptyStats();
      }

      const usageData = data || [];

      const todayUsage = usageData.filter(u => new Date(u.timestamp) >= oneDayAgo);
      const weekUsage = usageData.filter(u => new Date(u.timestamp) >= oneWeekAgo);
      const monthUsage = usageData.filter(u => new Date(u.timestamp) >= oneMonthAgo);

      return {
        totalTokensToday: todayUsage.reduce((sum, u) => sum + (u.total_tokens || 0), 0),
        totalCostToday: todayUsage.reduce((sum, u) => sum + (parseFloat(u.estimated_cost) || 0), 0),
        totalTokensWeek: weekUsage.reduce((sum, u) => sum + (u.total_tokens || 0), 0),
        totalCostWeek: weekUsage.reduce((sum, u) => sum + (parseFloat(u.estimated_cost) || 0), 0),
        totalTokensMonth: monthUsage.reduce((sum, u) => sum + (u.total_tokens || 0), 0),
        totalCostMonth: monthUsage.reduce((sum, u) => sum + (parseFloat(u.estimated_cost) || 0), 0),
        recentUsage: usageData.slice(0, 10)
      };
    } catch (error) {
      console.error('Error in getUsageStats:', error);
      return this.getEmptyStats();
    }
  }

  async cleanupExpiredInsights(tenantId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('ai_insights')
        .delete()
        .eq('tenant_id', tenantId)
        .eq('status', 'pending')
        .lt('expires_at', new Date().toISOString())
        .select('id');

      if (error) {
        console.error('Error cleaning up expired insights:', error);
        return 0;
      }

      const deletedCount = data?.length || 0;
      if (deletedCount > 0) {
        console.log(`🧹 Cleaned up ${deletedCount} expired insights`);
      }

      return deletedCount;
    } catch (error) {
      console.error('Error in cleanupExpiredInsights:', error);
      return 0;
    }
  }

  subscribeToInsights(tenantId: string, callback: (insights: AIInsight[]) => void): () => void {
    const channel = supabase
      .channel('ai_insights_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ai_insights',
          filter: `tenant_id=eq.${tenantId}`
        },
        async () => {
          const insights = await this.getValidInsights(tenantId);
          callback(insights);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  private dbToInsight(db: AIInsightDB): AIInsight {
    return {
      id: db.id,
      type: db.insight_type as any,
      contactId: db.contact_id || undefined,
      title: db.title,
      description: db.description,
      confidence: db.confidence,
      data: db.data,
      createdAt: new Date(db.created_at),
      status: db.status
    };
  }

  private getEmptyStats(): UsageStats {
    return {
      totalTokensToday: 0,
      totalCostToday: 0,
      totalTokensWeek: 0,
      totalCostWeek: 0,
      totalTokensMonth: 0,
      totalCostMonth: 0,
      recentUsage: []
    };
  }
}

export const aiInsightsService = new AIInsightsService();
export type { AIUsageTracking, UsageStats };
